import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/** Pull invite token from the URL; mail apps sometimes mangle query strings. */
const extractInviteToken = () => {
  const params = new URLSearchParams(window.location.search);
  const fromQuery = (params.get('invite') || '').replace(/[^a-zA-Z0-9]/g, '');
  if (fromQuery) return fromQuery;

  // Fallback: some in-app browsers strip search params on first load
  const stored = sessionStorage.getItem('pendingInviteToken');
  if (stored) return stored.replace(/[^a-zA-Z0-9]/g, '');

  return '';
};

export default function Register({ onSwitchToLogin, onBackToLanding }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedEULA, setAcceptedEULA] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingInvite, setValidatingInvite] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [inviteToken, setInviteToken] = useState(null);
  const [alreadyUsed, setAlreadyUsed] = useState(false);
  const { register } = useAuth();
  const { teamSettings } = useTheme();

  const validateInvitation = async (token) => {
    setValidatingInvite(true);
    setAlreadyUsed(false);
    try {
      const response = await fetch(
        `${API_URL}/api/invitations/validate?token=${encodeURIComponent(token)}`
      );
      let data = {};
      try {
        data = await response.json();
      } catch {
        throw new Error('non_json_response');
      }

      if (response.ok && data.valid) {
        setInvitation(data.invitation);
        setEmail(data.invitation.email);
        setError('');
        sessionStorage.setItem('pendingInviteToken', token);
      } else if (data.alreadyUsed) {
        setAlreadyUsed(true);
        setError(data.error || 'This invitation has already been used.');
      } else {
        setError(data.error || 'Invalid or expired invitation. Please request a new invitation from your club administrator.');
      }
    } catch (err) {
      console.error('Error validating invitation:', err);
      if (err.message === 'non_json_response') {
        setError('Could not reach the server to validate your invitation. Check your connection and tap Try Again — or copy the invite link from your email into Safari/Chrome directly.');
      } else {
        setError('Failed to validate invitation. Check your connection and try again.');
      }
    } finally {
      setValidatingInvite(false);
    }
  };

  // Get invite token from URL and validate it
  useEffect(() => {
    const token = extractInviteToken();

    if (!token) {
      setValidatingInvite(false);
      setError('An invitation is required to register. Please contact your club administrator to receive an invitation.');
      return;
    }

    setInviteToken(token);
    validateInvitation(token);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!invitation) {
      setError('Invalid invitation. Please use the link from your invitation email.');
      return;
    }

    if (!invitation.existingAccount && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!invitation.existingAccount && password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (!acceptedTerms) {
      setError('You must accept the Terms and Conditions to register');
      return;
    }

    if (!acceptedEULA) {
      setError('You must accept the End User License Agreement to register');
      return;
    }

    setLoading(true);

    const result = await register(
      email,
      password,
      invitation.existingAccount ? (name.trim() || undefined) : name,
      acceptedTerms,
      acceptedEULA,
      inviteToken
    );

    if (!result.success) {
      setError(result.error);
    } else {
      sessionStorage.removeItem('pendingInviteToken');
    }

    setLoading(false);
  };

  // Show loading while validating invite
  if (validatingInvite) {
    return (
      <div className="min-h-dvh bg-theme-bg flex items-center justify-center p-4 sm:p-6 safe-area-x pt-[max(1.5rem,env(safe-area-inset-top,0.5rem))]">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg w-full max-w-md text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Validating invitation...</p>
        </div>
      </div>
    );
  }

  // Show error if no valid invitation
  if (!invitation) {
    return (
      <div className="min-h-dvh bg-theme-bg flex items-center justify-center p-4 sm:p-6 safe-area-x pt-[max(1.5rem,env(safe-area-inset-top,0.5rem))]">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg w-full max-w-md">
          {onBackToLanding && (
            <button
              onClick={onBackToLanding}
              className="flex items-center gap-2 text-gray-600 hover:text-theme-primary mb-4 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="text-sm font-medium">Back</span>
            </button>
          )}
          <div className="text-center">
            <svg className="w-16 h-16 text-amber-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {alreadyUsed ? 'Already Registered' : 'Invitation Required'}
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={onSwitchToLogin}
                className="w-full btn-theme-primary px-6 py-3 rounded-lg font-semibold"
              >
                {alreadyUsed ? 'Go to Login' : 'Go to Login'}
              </button>
              {!alreadyUsed && inviteToken && (
                <button
                  type="button"
                  onClick={() => validateInvitation(inviteToken)}
                  className="w-full border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50"
                >
                  Try Again
                </button>
              )}
              {onBackToLanding && (
                <button
                  onClick={onBackToLanding}
                  className="w-full border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50"
                >
                  Back to Home
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-theme-bg flex items-center justify-center p-4 sm:p-6 safe-area-x pt-[max(1.5rem,env(safe-area-inset-top,0.5rem))]">
      <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg w-full max-w-md">
        {onBackToLanding && (
          <button
            onClick={onBackToLanding}
            className="flex items-center gap-2 text-gray-600 hover:text-theme-primary mb-4 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-sm font-medium">Back</span>
          </button>
        )}
        <h2 className="text-2xl sm:text-3xl font-bold text-theme-primary mb-2 text-center">
          {invitation.clubDisplayName || invitation.clubCode}
        </h2>
        <p className="text-center text-gray-600 mb-6">
          {invitation.alreadyMember
            ? 'You already have a membership here'
            : invitation.existingAccount
              ? 'Join with your existing RowCentral account'
              : 'Complete your registration'}
        </p>

        {/* Invitation Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800 font-medium mb-2">You've been invited to join:</p>
          <div className="space-y-1 text-sm text-blue-700">
            <p><strong>Club:</strong> {invitation.clubDisplayName || invitation.clubCode}</p>
            {invitation.team && <p><strong>Team:</strong> {invitation.team}</p>}
            <p><strong>Role:</strong> {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {invitation.alreadyMember ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 text-center">
              This email is already a member of this club. Log in with your existing password.
            </p>
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="w-full btn-theme-primary px-6 py-3 rounded-lg font-semibold"
            >
              Go to Login
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2 text-theme-primary">
              Email
            </label>
            <input
              type="email"
              value={email}
              className="border p-3 rounded w-full text-gray-500 bg-gray-100 cursor-not-allowed"
              disabled
            />
            <p className="text-xs text-gray-500 mt-1">
              Email is set from your invitation and cannot be changed
            </p>
          </div>

          {!invitation.existingAccount && (
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2 text-theme-primary">
              Your Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="border p-3 rounded w-full text-theme-primary bg-white focus-theme-primary"
              placeholder="Enter your full name"
              required
              disabled={loading}
            />
          </div>
          )}

          <div className={invitation.existingAccount ? 'mb-6' : 'mb-4'}>
            <label className="block text-sm font-semibold mb-2 text-theme-primary">
              {invitation.existingAccount ? 'Existing Password' : 'Password'}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="border p-3 rounded w-full text-theme-primary bg-white focus-theme-primary"
              placeholder={invitation.existingAccount ? 'Password for your RowCentral account' : 'At least 8 characters'}
              required
              disabled={loading}
            />
          </div>

          {!invitation.existingAccount && (
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-2 text-theme-primary">
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="border p-3 rounded w-full text-theme-primary bg-white focus-theme-primary"
              placeholder="Confirm your password"
              required
              disabled={loading}
            />
          </div>
          )}

          <div className="mb-6 space-y-3">
            <div className="flex items-start">
              <input
                type="checkbox"
                id="acceptTerms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 h-4 w-4 text-theme-primary border-gray-300 rounded focus:ring-theme-primary"
                disabled={loading}
              />
              <label htmlFor="acceptTerms" className="ml-2 text-sm text-gray-700">
                I accept the{' '}
                <a 
                  href="/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-theme-primary hover:underline font-semibold"
                >
                  Terms and Conditions
                </a>
              </label>
            </div>
            <div className="flex items-start">
              <input
                type="checkbox"
                id="acceptEULA"
                checked={acceptedEULA}
                onChange={(e) => setAcceptedEULA(e.target.checked)}
                className="mt-1 h-4 w-4 text-theme-primary border-gray-300 rounded focus:ring-theme-primary"
                disabled={loading}
              />
              <label htmlFor="acceptEULA" className="ml-2 text-sm text-gray-700">
                I accept the{' '}
                <a 
                  href="/eula" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-theme-primary hover:underline font-semibold"
                >
                  End User License Agreement (EULA)
                </a>
              </label>
            </div>
          </div>

          <button
            type="submit"
            className="w-full btn-theme-primary px-6 py-3 rounded-lg font-semibold disabled:bg-gray-400 disabled:opacity-50"
            disabled={loading}
          >
            {loading
              ? (invitation.existingAccount ? 'Joining Club...' : 'Creating Account...')
              : (invitation.existingAccount ? 'Join Club' : 'Create Account')}
          </button>
        </form>
        )}

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <button
              onClick={onSwitchToLogin}
              className="text-theme-primary font-semibold hover:underline"
            >
              Login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
